export type FinanceRange = "day" | "week" | "month" | "year";

const moveUtcDays = (date: Date, amount: number) => new Date(date.getTime() + amount * 86_400_000);
const utcIso = (date: Date) => date.toISOString().slice(0, 10);

export const financePeriodFor = (anchor: string, range: FinanceRange) => {
  const date = new Date(`${anchor}T12:00:00Z`);
  let from = date;
  let to = date;
  let previousFrom = moveUtcDays(date, -1);
  let previousTo = previousFrom;

  if (range === "week") {
    const daysFromMonday = (date.getUTCDay() + 6) % 7;
    from = moveUtcDays(date, -daysFromMonday);
    to = moveUtcDays(from, 6);
    previousFrom = moveUtcDays(from, -7);
    previousTo = moveUtcDays(to, -7);
  } else if (range === "month") {
    from = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));
    to = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12));
    previousFrom = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1, 12));
    previousTo = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 0, 12));
  } else if (range === "year") {
    from = new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 12));
    to = new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 12));
    previousFrom = new Date(Date.UTC(date.getUTCFullYear() - 1, 0, 1, 12));
    previousTo = new Date(Date.UTC(date.getUTCFullYear() - 1, 11, 31, 12));
  }

  const short = (value: Date) => value.toLocaleDateString("pt-BR", {
    timeZone: "UTC", day: "2-digit", month: "short",
  }).replace(".", "");
  const label = range === "day"
    ? date.toLocaleDateString("pt-BR", { timeZone: "UTC", day: "2-digit", month: "long", year: "numeric" })
    : range === "week" ? `${short(from)} – ${short(to)} ${to.getUTCFullYear()}`
      : range === "year" ? String(date.getUTCFullYear())
        : date.toLocaleDateString("pt-BR", { timeZone: "UTC", month: "long", year: "numeric" });

  return {
    from: utcIso(from), to: utcIso(to), previousFrom: utcIso(previousFrom), previousTo: utcIso(previousTo), label,
    previousLabel: range === "day" ? "dia anterior" : range === "week" ? "semana anterior" : range === "year" ? "ano anterior" : "mês anterior",
    receivedLabel: range === "day" ? "Recebido no dia" : range === "week" ? "Recebido na semana" : range === "year" ? "Recebido no ano" : "Recebido no mês",
    servicesLabel: range === "day" ? "Serviços do dia" : range === "week" ? "Serviços da semana" : range === "year" ? "Serviços do ano" : "Serviços do mês",
    costsLabel: range === "day" ? "Custos do dia" : range === "week" ? "Custos da semana" : range === "year" ? "Custos do ano" : "Custos do mês",
    outflowsLabel: range === "day" ? "Saídas do dia" : range === "week" ? "Saídas da semana" : range === "year" ? "Saídas do ano" : "Saídas do mês",
    balanceLabel: range === "day" ? "Saldo do dia" : range === "week" ? "Saldo da semana" : range === "year" ? "Saldo do ano" : "Saldo do mês",
    resultLabel: range === "day" ? "Resultado do dia" : range === "week" ? "Resultado da semana" : range === "year" ? "Resultado do ano" : "Resultado do mês",
  };
};

export const financeFlowRanges = (from: string, to: string, range: FinanceRange) => {
  const start = new Date(`${from}T12:00:00Z`);
  const end = new Date(`${to}T12:00:00Z`);
  if (range === "day") return [{ key: from, label: "No dia", from, to }];
  const points: Array<{ key: string; label: string; from: string; to: string }> = [];
  let cursor = start;
  while (cursor <= end) {
    const bucketStart = cursor;
    const monthEnd = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0, 12));
    const bucketEnd = range === "week"
      ? cursor
      : new Date(Math.min((range === "year" ? monthEnd : moveUtcDays(cursor, 6)).getTime(), end.getTime()));
    const bucketFrom = utcIso(bucketStart);
    const bucketTo = utcIso(bucketEnd);
    points.push({
      key: `${bucketFrom}:${bucketTo}`,
      label: range === "week"
        ? bucketStart.toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short" }).replace(".", "")
        : range === "year"
          ? bucketStart.toLocaleDateString("pt-BR", { timeZone: "UTC", month: "short" }).replace(".", "")
        : `${String(bucketStart.getUTCDate()).padStart(2, "0")}–${String(bucketEnd.getUTCDate()).padStart(2, "0")}`,
      from: bucketFrom,
      to: bucketTo,
    });
    cursor = moveUtcDays(bucketEnd, 1);
  }
  return points;
};
